import React, { useState } from 'react';
import { Plus, Minus } from 'lucide-react';
import { useReveal } from '../hooks/useInView';

const FAQS = [
  {
    q: 'What is GLP-1 / GIP and is it safe?',
    a: 'GLP-1 / GIP medications mimic natural gut hormones that regulate appetite and blood sugar. They\'re approved by the FDA, EMA, Singapore HSA, and Malaysia\'s NPRA for weight management — especially for people with elevated BMI or Type 2 diabetes. Like any medication, they have side effects (most commonly nausea or fatigue early on). Your assigned doctor reviews your full history before any injection.'
  },
  {
    q: 'Who is eligible for treatment?',
    a: 'In Malaysia, GLP-1 / GIP treatments are typically appropriate for adults with BMI ≥ 27, or BMI ≥ 25 with metabolic issues (high blood sugar, hypertension, etc.). The onboarding quiz screens for both. People with a personal or family history of medullary thyroid carcinoma, multiple endocrine neoplasia type 2, severe GLP-1 allergies, or who are pregnant / breastfeeding are not eligible.'
  },
  {
    q: 'How does the booking process work?',
    a: 'Sign up, take the 2-minute eligibility quiz, and we shortlist verified clinics near you. Pick a date and pay a refundable deposit to lock the slot. You receive a QR code for clinic check-in. The doctor reviews your case at the clinic before any injection.'
  },
  {
    q: 'How are payments handled?',
    a: 'Deposits and payments are processed securely through Curlec (Razorpay Malaysia) — supporting FPX, Visa, Mastercard, and most local Malaysian e-wallets. No subscriptions; you pay per plan. Final pricing is confirmed by the clinic after the doctor\'s review.'
  },
  {
    q: 'How long does a course take?',
    a: 'Each bottle is 20 mg, 4 weekly injections — so a 1-bottle plan runs 4 weeks, 2-bottle plan runs 8 weeks, and 3-bottle plan runs 12 weeks. Most people see 2 – 4 kg loss per bottle, but this varies by adherence, lifestyle, and biology.'
  },
  {
    q: 'Is my health data private?',
    a: 'Yes. All data is encrypted at rest and in transit, stored on PDPA-aligned infrastructure, and accessible only to you and the clinic you book with. You can export or delete your records at any time. We will never sell your data.'
  },
  {
    q: 'How does the referral program work?',
    a: 'Each user gets a unique referral link. When someone you refer completes a booking, you earn RM30 in credits and they get RM50 off their first visit. Rewards only fire after a valid transaction — we block self-referrals and abuse.'
  }
];

function FAQItem({ item, isOpen, onToggle }) {
  return (
    <div className="border-b border-ink-200 last:border-0">
      <button
        className="w-full flex items-center justify-between gap-4 py-5 text-left"
        onClick={onToggle}
        aria-expanded={isOpen}
      >
        <span className="font-medium text-ink-900">{item.q}</span>
        <span className={`shrink-0 w-8 h-8 rounded-full flex items-center justify-center transition-colors ${isOpen ? 'bg-brand-700 text-white' : 'bg-ink-100 text-ink-700'}`}>
          {isOpen ? <Minus size={16} /> : <Plus size={16} />}
        </span>
      </button>
      <div
        className={`grid transition-all duration-300 ease-out ${isOpen ? 'grid-rows-[1fr] opacity-100 pb-5' : 'grid-rows-[0fr] opacity-0'}`}
      >
        <div className="overflow-hidden">
          <p className="text-ink-600 leading-relaxed">{item.a}</p>
        </div>
      </div>
    </div>
  );
}

export default function FAQ() {
  const [openIdx, setOpenIdx] = useState(0);
  const head = useReveal();

  return (
    <section id="faq" className="section bg-white">
      <div className="container-x">
        <div className="grid lg:grid-cols-12 gap-12">
          <div ref={head.ref} className={`${head.className} lg:col-span-5`}>
            <span className="eyebrow">FAQ</span>
            <h2 className="h-section mt-4">Real answers, in plain English.</h2>
            <p className="lead mt-4">
              Common questions about eligibility, safety, and how the platform works.
              Can't find yours? Our care team replies in under 24 hours.
            </p>
          </div>

          <div className="lg:col-span-7">
            <div className="card-soft px-6 sm:px-8">
              {FAQS.map((f, i) => (
                <FAQItem
                  key={f.q}
                  item={f}
                  isOpen={openIdx === i}
                  onToggle={() => setOpenIdx(openIdx === i ? -1 : i)}
                />
              ))}
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
